import { createFileRoute } from '@tanstack/react-router';
import { ArrowRightIcon, CheckIcon, CopyIcon, ExternalLinkIcon } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_authed/mcp-setup')({
  component: McpSetupPage,
});

/**
 * In-app version of the MCP setup guide. Mirrors the standalone HTML at
 * `apps/mcp/docs/setup.html` (which is meant for email / Notion sharing),
 * but rendered inside the Data Hub app shell so it's discoverable from
 * the sidebar. Uses the Robot.com brand palette via arbitrary Tailwind
 * values — the page intentionally reads as a "branded landing" inside
 * the otherwise shadcn-themed app, because it's marketing-style content.
 */
function McpSetupPage() {
  return (
    <div className="bg-white text-neutral-900">
      <div className="mx-auto max-w-3xl px-6 py-12 md:py-16">
        <Hero />
        <WhatYouCanDo />
        <Steps />
        <SecurityCallout />
        <Tools />
        <Troubleshooting />
        <Resources />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="border-b border-neutral-200 pb-12">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
        Data Hub MCP
      </p>
      <h1 className="font-sans text-4xl font-semibold tracking-tight text-neutral-900 md:text-5xl md:leading-[1.05]">
        Connect Claude to the Data Hub.
      </h1>
      <p className="mt-5 max-w-xl text-lg leading-relaxed text-neutral-600">
        Search, open and create resources from your AI assistant — no browser switching, no clones,
        no terminal. Two minutes end to end.
      </p>
      <div className="mt-7 flex flex-wrap items-center gap-3">
        <a
          href="/admin/tokens"
          className="inline-flex items-center gap-2 rounded-md bg-[#FFF65D] px-6 py-3 text-base font-semibold text-neutral-900 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
        >
          Get your token
          <ArrowRightIcon className="size-4" />
        </a>
        <a
          href="#setup"
          className="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-5 py-[11px] text-sm font-medium text-neutral-900 transition hover:bg-neutral-50"
        >
          See the steps
        </a>
      </div>
      <dl className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-sm text-neutral-500">
        <div>
          <dt className="inline font-semibold text-neutral-900">Time</dt>{' '}
          <dd className="inline">· 2 min</dd>
        </div>
        <div>
          <dt className="inline font-semibold text-neutral-900">Requires</dt>{' '}
          <dd className="inline">· Claude Code or Claude Desktop</dd>
        </div>
        <div>
          <dt className="inline font-semibold text-neutral-900">Doesn't need</dt>{' '}
          <dd className="inline">· cloning the repo, terminal expertise</dd>
        </div>
      </dl>
    </section>
  );
}

function WhatYouCanDo() {
  const examples = [
    {
      label: 'Search',
      body: '"Show me the active dashboards for the DATA area" → Claude calls search_resources and returns the list.',
    },
    {
      label: 'Detail',
      body: '"Open that Looker one you mentioned" → Claude uses get_resource and gives you the URL, description, tags, owner.',
    },
    {
      label: 'Categories',
      body: '"What resource categories exist" → Claude lists every category so you can filter or pick one when creating.',
    },
    {
      label: 'Create',
      body: '"Add this link as a shared resource with DATA: …" → Claude creates the resource attributed to you.',
    },
  ];

  return (
    <section className="border-b border-neutral-200 py-12">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
        What you can do
      </p>
      <h2 className="font-sans text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
        Ask naturally. Claude does the work.
      </h2>
      <p className="mt-3 max-w-2xl text-base text-neutral-600">
        Once connected, Claude has 5 actions available against the Data Hub. It picks the right one
        based on what you ask in plain language — you don't invoke anything manually.
      </p>
      <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {examples.map((ex) => (
          <li
            key={ex.label}
            className="rounded-2xl bg-neutral-50 p-6 ring-1 ring-inset ring-neutral-100"
          >
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-neutral-500">
              {ex.label}
            </span>
            <p className="text-[15px] leading-relaxed text-neutral-800">{ex.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Steps() {
  return (
    <section id="setup" className="scroll-mt-8 border-b border-neutral-200 py-12">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
        Setup
      </p>
      <h2 className="font-sans text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
        Four steps.
      </h2>
      <p className="mt-3 max-w-2xl text-base text-neutral-600">
        If you already have Claude Code or Claude Desktop installed, you have everything you need.
        Go.
      </p>

      <ol className="mt-8 space-y-4">
        <Step number={1} title="Get your token">
          <p>
            Go to{' '}
            <a
              href="/admin/tokens"
              className="font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
            >
              /admin/tokens
            </a>{' '}
            and create a token with the scopes you need:
          </p>
          <ul className="my-3 list-disc pl-6 text-[15px] text-neutral-700">
            <li>
              <strong className="font-semibold">resource:read</strong> — read-only: search and open
            </li>
            <li>
              <strong className="font-semibold">resource:read</strong> and{' '}
              <strong className="font-semibold">resource:write</strong> — full access: also create
              and edit
            </li>
          </ul>
          <p>
            The{' '}
            <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
              dth_…
            </code>{' '}
            token is shown <strong className="font-semibold">only once</strong>. Copy it now and
            store it safely — if you lose it, just generate a new one.
          </p>
        </Step>

        <Step number={2} title="Add the MCP to Claude">
          <p className="mb-3">
            In your terminal, run this — replace{' '}
            <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
              dth_REPLACE
            </code>{' '}
            with your actual token:
          </p>
          <CodeBlock
            copy={
              'claude mcp add -t http -s user data-hub https://datahub.mcp.kiwibot.com/mcp --header "Authorization: Bearer dth_REPLACE"'
            }
          >
            <span className="text-neutral-500"># Claude Code (Linux / macOS / Windows + WSL)</span>
            {'\n'}
            claude mcp add -t http -s user data-hub https://datahub.mcp.kiwibot.com/mcp --header{' '}
            <span className="font-semibold text-[#FFF65D]">
              "Authorization: Bearer dth_REPLACE"
            </span>
          </CodeBlock>
          <p>
            Using <strong className="font-semibold">Claude Desktop</strong> instead? Edit your
            config file and add the same URL under{' '}
            <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
              mcpServers
            </code>
            . See the README in the repo for the exact JSON shape.
          </p>
        </Step>

        <Step number={3} title="Restart Claude">
          <p className="mb-3">
            Close Claude Code (or Desktop) completely and open it again. MCP tools load on startup —
            without a restart, they won't appear.
          </p>
          <p className="mb-3">Confirm the registration:</p>
          <CodeBlock copy="claude mcp list">
            claude mcp list{'\n'}
            <span className="text-neutral-500">
              # → data-hub: https://datahub.mcp.kiwibot.com/mcp
            </span>
          </CodeBlock>
        </Step>

        <Step number={4} title="Test it">
          <p className="mb-3">In a new session, ask Claude something concrete:</p>
          <CodeBlock
            copy={
              '"List the Data Hub categories, then give me 3 sample resources from the first category."'
            }
          >
            <span className="text-neutral-500"># Sample prompt</span>
            {'\n'}
            "List the Data Hub categories, then give me 3{'\n'}
            sample resources from the first category."
          </CodeBlock>
          <p>
            If Claude calls{' '}
            <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
              mcp__data-hub__list_categories
            </code>{' '}
            and brings back real data, you're connected.{' '}
            <strong className="font-semibold">Welcome aboard.</strong>
          </p>
        </Step>
      </ol>
    </section>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="rounded-3xl border border-neutral-200 bg-white p-6 md:p-8">
      <div className="flex items-start gap-5 md:gap-6">
        <div
          aria-hidden="true"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-lg font-semibold text-white md:size-12 md:text-xl"
        >
          {number}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="mb-2 text-xl font-medium tracking-tight text-neutral-900 md:text-[22px]">
            {title}
          </h3>
          <div className="space-y-1 text-[15px] leading-relaxed text-neutral-700">{children}</div>
        </div>
      </div>
    </li>
  );
}

function CodeBlock({ children, copy }: { children: React.ReactNode; copy?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!copy) return;
    try {
      await navigator.clipboard.writeText(copy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers / insecure contexts can reject clipboard writes.
      // Silently no-op — the visible code is still selectable by hand.
    }
  };
  return (
    <div className="group relative my-4">
      <pre className="overflow-x-auto rounded-lg bg-neutral-900 px-5 py-4 pr-14 font-mono text-[13.5px] leading-relaxed text-neutral-100">
        <code>{children}</code>
      </pre>
      {copy && (
        <button
          type="button"
          onClick={onCopy}
          aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
          title={copied ? 'Copied' : 'Copy'}
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md bg-neutral-800 text-neutral-300 opacity-0 transition hover:bg-neutral-700 hover:text-white focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFF65D] group-hover:opacity-100"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-[#FFF65D]" aria-hidden="true" />
          ) : (
            <CopyIcon className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );
}

function SecurityCallout() {
  return (
    <section className="py-10">
      <div className="rounded-xl border-l-4 border-[#FFF65D] bg-neutral-50 px-7 py-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
          Security — read this
        </p>
        <p className="text-[15px] leading-relaxed text-neutral-700">
          The{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">dth_…</code>{' '}
          token is a credential. Treat it like an SSH key:
        </p>
        <ul className="mt-3 list-disc space-y-1.5 pl-6 text-[15px] text-neutral-700">
          <li>
            <strong className="font-semibold">Don't paste it in chats</strong> (Slack, email, AI
            chats). If you do by mistake, revoke it at{' '}
            <a
              href="/admin/tokens"
              className="font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
            >
              /admin/tokens
            </a>{' '}
            and create a new one.
          </li>
          <li>
            <strong className="font-semibold">Don't commit it</strong> to a repo. Your Claude config
            lives only on your disk.
          </li>
          <li>
            <strong className="font-semibold">The bot acts "as you"</strong> in activity logs and
            resource attribution, but with downgraded permissions — it can never perform admin
            actions.
          </li>
        </ul>
      </div>
    </section>
  );
}

function Tools() {
  const rows: Array<{ name: string; scope: 'read' | 'write'; desc: string }> = [
    {
      name: 'search_resources',
      scope: 'read',
      desc: 'Lists resources with optional filters (category, source, visibility). Paginated.',
    },
    {
      name: 'get_resource',
      scope: 'read',
      desc: 'Fetches a single resource by id, with full description, tags, sharing.',
    },
    {
      name: 'list_categories',
      scope: 'read',
      desc: 'Returns every category so you can filter or categorize when creating.',
    },
    {
      name: 'create_resource',
      scope: 'write',
      desc: "Creates a new resource, attributed to the token's owner.",
    },
    {
      name: 'update_resource',
      scope: 'write',
      desc: "Edits a resource you created (or any, if you're admin).",
    },
  ];

  return (
    <section className="border-t border-neutral-200 py-12">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
        Reference
      </p>
      <h2 className="font-sans text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
        The 5 tools Claude can use.
      </h2>
      <p className="mt-3 max-w-2xl text-base text-neutral-600">
        You don't have to memorize them — Claude picks the right one based on your ask. This is just
        so you know what's available.
      </p>
      <div className="mt-6 overflow-hidden rounded-lg ring-1 ring-neutral-200">
        <table className="w-full text-[15px]">
          <thead>
            <tr className="bg-neutral-50">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-neutral-500">
                Tool
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-neutral-500">
                Scope
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-neutral-500">
                What it does
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.name} className={i > 0 ? 'border-t border-neutral-100' : ''}>
                <td className="px-4 py-3 align-top font-mono text-[14px] font-semibold text-neutral-900">
                  {r.name}
                </td>
                <td className="px-4 py-3 align-top">
                  <span
                    className={
                      r.scope === 'write'
                        ? 'inline-flex rounded-full bg-neutral-900 px-2 py-0.5 font-mono text-[12px] font-semibold text-white'
                        : 'inline-flex rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[12px] font-semibold text-neutral-700'
                    }
                  >
                    resource:{r.scope}
                  </span>
                </td>
                <td className="px-4 py-3 align-top text-neutral-700">{r.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Troubleshooting() {
  const items: Array<{ summary: string; body: React.ReactNode }> = [
    {
      summary: '"401 Invalid, expired, or revoked API key"',
      body: (
        <p>
          The token was revoked or expired. Go to{' '}
          <a
            href="/admin/tokens"
            className="font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
          >
            /admin/tokens
          </a>
          , create a new one, then update your config with{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
            claude mcp remove data-hub
          </code>{' '}
          followed by the{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">add</code>{' '}
          command from step 2.
        </p>
      ),
    },
    {
      summary: '"401 missing required scope(s): resource:write"',
      body: (
        <p>
          Your token only has{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
            resource:read
          </code>
          . Scopes can't be edited after creation — generate a new token with both scopes and update
          your config.
        </p>
      ),
    },
    {
      summary: '"401 Missing or malformed Authorization header"',
      body: (
        <p>
          Your MCP client isn't forwarding the header. Re-run the step 2 command — the{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
            --header
          </code>{' '}
          flag is what configures it.
        </p>
      ),
    },
    {
      summary: "Claude doesn't show the tools after adding the MCP",
      body: (
        <p>
          Claude loads tools on startup, not live. Close the app completely and reopen. If they
          still don't appear,{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
            claude mcp list
          </code>{' '}
          should show{' '}
          <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]">
            data-hub
          </code>{' '}
          — if it doesn't, the add didn't register.
        </p>
      ),
    },
    {
      summary: '"Pick an area on your Profile before creating a resource"',
      body: (
        <p>
          The backend requires you to have an area assigned on your profile before you can create
          resources. Go to your{' '}
          <a
            href="/profile"
            className="font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
          >
            profile
          </a>
          , set your area (DATA, ENG, etc.), and retry. The token itself doesn't need to change.
        </p>
      ),
    },
    {
      summary: 'Something else',
      body: (
        <p>
          Paste the full error to the Data team on Slack or open an issue in the repo. The MCP's
          error messages include the backend's detail — if you say "it doesn't work" we can't help;
          if you paste the message, we can.
        </p>
      ),
    },
  ];

  return (
    <section className="border-t border-neutral-200 py-12">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
        Troubleshooting
      </p>
      <h2 className="font-sans text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
        If something breaks.
      </h2>
      <p className="mt-3 max-w-2xl text-base text-neutral-600">
        The most common errors and how to unblock them.
      </p>
      <div className="mt-6">
        {items.map((item, i) => (
          <details
            key={item.summary}
            className={
              i === 0 ? 'border-y border-neutral-200 py-4' : 'border-b border-neutral-200 py-4'
            }
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-neutral-900 [&::-webkit-details-marker]:hidden">
              <span>{item.summary}</span>
              <span
                aria-hidden="true"
                className="text-xl font-normal text-neutral-500 transition group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <div className="mt-3 text-[15px] leading-relaxed text-neutral-700">{item.body}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

function Resources() {
  return (
    <section className="border-t border-neutral-200 py-12">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
        More
      </p>
      <h2 className="font-sans text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
        Going deeper.
      </h2>
      <ul className="mt-5 space-y-2 text-[15px] text-neutral-700">
        <li>
          <a
            href="https://github.com/kiwicampus/data-hub/tree/main/apps/mcp"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
          >
            Repo source &amp; README <ExternalLinkIcon className="size-3.5" />
          </a>{' '}
          — full setup reference, deploy guide, and development workflow.
        </li>
        <li>
          <a
            href="https://github.com/kiwicampus/data-hub/blob/main/apps/mcp/docs/setup.html"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
          >
            Shareable HTML version <ExternalLinkIcon className="size-3.5" />
          </a>{' '}
          — single-file copy of this page for email, Slack, or Notion.
        </li>
        <li>
          <a
            href="https://modelcontextprotocol.io"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[3px] hover:decoration-neutral-900"
          >
            About the Model Context Protocol <ExternalLinkIcon className="size-3.5" />
          </a>{' '}
          — what MCP is, why it exists, and which clients support it.
        </li>
      </ul>
    </section>
  );
}
